import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockResetIcon from '@mui/icons-material/LockReset';
import FlagCircleIcon from '@mui/icons-material/FlagCircle';
import Snackbar from '@mui/material/Snackbar';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { listInterns, listColleges, updateInternStatus, resetInternPassword, clearInternCancellationFlag, resetInternTest, getInternVideo, reviewInternVideo } from '../services/adminApi';
import { formatDate } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ScrollableChipRow from '../components/common/ScrollableChipRow';

const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Pending Video Review', value: 'pending_review' },
  { label: 'Pending Assessment', value: 'pending_assessment' },
  { label: 'Pending Video', value: 'pending_video' },
  { label: 'Background Check', value: 'pending_background_check' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Suspended', value: 'suspended' },
];

// Maps the live intern record to its effective video-review state.
// Synthesises 'none' when no video_url is on file so the column / chip
// can still render something useful instead of an empty cell.
function videoStateOf(intern) {
  if (!intern) return 'none';
  if (intern.video_status) return intern.video_status;
  if (intern.video_url) return 'pending';
  return 'none';
}

// Two-letter state the intern works from (collected at registration).
function stateOf(intern) {
  return ((intern?.address?.state) || '').trim().toUpperCase();
}

export default function InternsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [interns, setInterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [collegeFilter, setCollegeFilter] = useState('all');
  // college_id -> display name, from the Colleges table (e.g. gcu-001 ->
  // Grand Canyon University). Registration stores whatever the applicant
  // supplied in college_id: sometimes a partner code, sometimes free text.
  const [collegeMap, setCollegeMap] = useState({});
  const [selectedIntern, setSelectedIntern] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [reason, setReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  // Video review panel state — fetched on drawer open if a video is on file.
  const [videoDetail, setVideoDetail] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [rejectVideoOpen, setRejectVideoOpen] = useState(false);
  const [rejectVideoReason, setRejectVideoReason] = useState('');

  const fetchInterns = useCallback(() => {
    setLoading(true);
    const filters = filter !== 'all' ? { status: filter } : {};
    listInterns(filters)
      .then((data) => setInterns(data.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    fetchInterns();
  }, [fetchInterns]);

  // Load the colleges directory once for id -> name resolution.
  useEffect(() => {
    listColleges()
      .then((data) => {
        const map = {};
        (data.items || data.colleges || []).forEach((c) => {
          if (c.college_id && c.name) map[c.college_id] = c.name;
        });
        setCollegeMap(map);
      })
      .catch(() => {}); // non-fatal -- raw ids still display
  }, []);

  // Registration stores the college as free text (or a partner code like
  // gcu-001), so the same school shows up under many spellings. Normalize
  // to a canonical key so the filter groups them: resolve partner codes,
  // trim/collapse whitespace, drop apostrophes, lowercase, and fold the
  // frequent "WGU" spelling family into one school.
  const collegeKeyOf = useCallback((intern) => {
    const raw = intern?.college_name || intern?.college || intern?.college_id || '';
    const resolved = (collegeMap[raw] || raw).trim().replace(/\s+/g, ' ');
    let key = resolved.toLowerCase().replace(/[’']/g, '');
    if (key === 'wgu' || key.startsWith('western governor')) {
      key = 'western governors university';
    }
    return key;
  }, [collegeMap]);

  // key -> display name (the most common raw spelling seen in the data),
  // plus the sorted option list for the dropdown.
  const collegeIndex = useMemo(() => {
    const variants = {}; // key -> {spelling: count}
    interns.forEach((i) => {
      const key = collegeKeyOf(i);
      if (!key) return;
      const raw = (collegeMap[i.college_name || i.college || i.college_id] ||
        i.college_name || i.college || i.college_id || '').trim().replace(/\s+/g, ' ');
      variants[key] = variants[key] || {};
      variants[key][raw] = (variants[key][raw] || 0) + 1;
    });
    const display = {};
    Object.entries(variants).forEach(([key, spellings]) => {
      display[key] = Object.entries(spellings).sort((a, b) => b[1] - a[1])[0][0];
    });
    const options = Object.keys(display)
      .map((key) => ({ key, label: display[key] }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { display, options };
  }, [interns, collegeKeyOf, collegeMap]);

  // Canonical display name for a row's college.
  const collegeOf = useCallback(
    (intern) => collegeIndex.display[collegeKeyOf(intern)] || '',
    [collegeIndex, collegeKeyOf]
  );

  // Dropdown options derived from the loaded rows, so they always reflect
  // real data (no stale hardcoded lists).
  const stateOptions = useMemo(() => {
    const s = new Set(interns.map(stateOf).filter(Boolean));
    return [...s].sort();
  }, [interns]);

  // Search (name/email) + state + college, applied client-side over the
  // already-loaded list. Status filtering stays server-side via the chips.
  const filteredInterns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return interns.filter((i) => {
      if (q) {
        const name = (i.name || `${i.first_name || ''} ${i.last_name || ''}`).toLowerCase();
        const email = (i.email || '').toLowerCase();
        if (!name.includes(q) && !email.includes(q)) return false;
      }
      if (stateFilter !== 'all' && stateOf(i) !== stateFilter) return false;
      if (collegeFilter !== 'all' && collegeKeyOf(i) !== collegeFilter) return false;
      return true;
    });
  }, [interns, search, stateFilter, collegeFilter, collegeKeyOf]);

  const handleRowClick = (params) => {
    setSelectedIntern(params.row);
    setDrawerOpen(true);
    // Pull the latest video detail server-side so the panel reflects
    // changes since the row was last listed (e.g. an upload after the
    // list was rendered).
    const id = params.row.id || params.row.intern_id;
    if (id && (params.row.video_url || params.row.video_status)) {
      setVideoLoading(true);
      setVideoDetail(null);
      getInternVideo(id)
        .then((data) => setVideoDetail(data))
        .catch((err) => {
          // Non-fatal — just show what we have on the row.
          console.error('getInternVideo failed', err);
        })
        .finally(() => setVideoLoading(false));
    } else {
      setVideoDetail(null);
    }
  };

  const refreshVideoDetail = (id) => {
    if (!id) return;
    setVideoLoading(true);
    getInternVideo(id)
      .then((data) => setVideoDetail(data))
      .catch(() => {})
      .finally(() => setVideoLoading(false));
  };

  const handleApproveVideo = async () => {
    if (!selectedIntern) return;
    const id = selectedIntern.id || selectedIntern.intern_id;
    setActionLoading(true);
    try {
      const result = await reviewInternVideo(id, 'approve');
      setSnack({
        open: true,
        severity: 'success',
        message: result.message || 'Video approved.',
      });
      // Update the row in the local list so the column reflects the change
      // immediately. Also refresh the drawer's video panel.
      setInterns((rows) =>
        rows.map((r) =>
          (r.id || r.intern_id) === id
            ? { ...r, video_status: 'approved', status: result.status || r.status }
            : r
        )
      );
      setSelectedIntern((cur) =>
        cur ? { ...cur, video_status: 'approved', status: result.status || cur.status } : cur
      );
      refreshVideoDetail(id);
    } catch (err) {
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || err.message || 'Failed to approve video',
      });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleRejectVideoSubmit = async () => {
    if (!selectedIntern) return;
    const id = selectedIntern.id || selectedIntern.intern_id;
    if (!rejectVideoReason.trim()) {
      setSnack({
        open: true,
        severity: 'warning',
        message: 'A rejection reason is required (the intern sees it in their email).',
      });
      return;
    }
    setActionLoading(true);
    try {
      const result = await reviewInternVideo(id, 'reject', rejectVideoReason.trim());
      setSnack({
        open: true,
        severity: 'success',
        message: result.message || 'Video rejected; intern notified.',
      });
      setInterns((rows) =>
        rows.map((r) =>
          (r.id || r.intern_id) === id
            ? { ...r, video_status: 'rejected', status: result.status || r.status }
            : r
        )
      );
      setSelectedIntern((cur) =>
        cur ? { ...cur, video_status: 'rejected', status: result.status || cur.status } : cur
      );
      refreshVideoDetail(id);
      setRejectVideoOpen(false);
      setRejectVideoReason('');
    } catch (err) {
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || err.message || 'Failed to reject video',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!selectedIntern) return;
    setActionLoading(true);
    try {
      await updateInternStatus(selectedIntern.id || selectedIntern.intern_id, status, reason);
      setDrawerOpen(false);
      setConfirmAction(null);
      setReason('');
      fetchInterns();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedIntern) return;
    setActionLoading(true);
    try {
      const result = await resetInternPassword(selectedIntern.id || selectedIntern.intern_id);
      setConfirmAction(null);
      setSnack({
        open: true,
        message: result.message || 'Password reset email sent.',
        severity: 'success',
      });
    } catch (err) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || err.message || 'Failed to reset password',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearCancelFlag = async () => {
    if (!selectedIntern) return;
    setActionLoading(true);
    try {
      const result = await clearInternCancellationFlag(
        selectedIntern.id || selectedIntern.intern_id,
        reason,
      );
      setConfirmAction(null);
      setReason('');
      setDrawerOpen(false);
      fetchInterns();
      setSnack({
        open: true,
        message: result.message || 'Cancellation flag cleared.',
        severity: 'success',
      });
    } catch (err) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || err.message || 'Failed to clear flag',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetTest = async () => {
    if (!selectedIntern) return;
    setActionLoading(true);
    try {
      const id = selectedIntern.id || selectedIntern.intern_id;
      const alsoUnlock = selectedIntern.status === 'test_failed_permanently';
      const result = await resetInternTest(id, { note: reason, alsoUnlockStatus: alsoUnlock });
      setConfirmAction(null);
      setReason('');
      setDrawerOpen(false);
      fetchInterns();
      setSnack({
        open: true,
        message: result.message || 'Test reset.',
        severity: 'success',
      });
    } catch (err) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || err.message || 'Failed to reset test',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        params.row.name || `${params.row.first_name || ''} ${params.row.last_name || ''}`.trim() || '-',
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'college', headerName: 'College', flex: 1, minWidth: 150, valueGetter: (params) => collegeOf(params.row) || '-' },
    { field: 'state', headerName: 'State', width: 80, valueGetter: (params) => stateOf(params.row) || '-' },
    { field: 'gpa', headerName: 'GPA', width: 80, valueGetter: (params) => params.row.gpa || '-' },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params) => <StatusChip status={params.value} type="intern" />,
    },
    {
      field: 'video',
      headerName: 'Video',
      width: 140,
      // Use a computed valueGetter so this column is sortable by the
      // synthesised state (none < pending < rejected < approved). The
      // chip itself is rendered via renderCell.
      valueGetter: (params) => videoStateOf(params.row),
      renderCell: (params) => <StatusChip status={params.value} type="videoStatus" />,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Interns
      </Typography>

      <ScrollableChipRow>
        {FILTER_OPTIONS.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            onClick={() => setFilter(opt.value)}
            color={filter === opt.value ? 'primary' : 'default'}
            variant={filter === opt.value ? 'filled' : 'outlined'}
          />
        ))}
      </ScrollableChipRow>

      {/* Search + state/college filters */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2, alignItems: { sm: 'center' } }}
      >
        <TextField
          size="small"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, backgroundColor: 'background.paper' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="State"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          sx={{ minWidth: 140, backgroundColor: 'background.paper' }}
        >
          <MenuItem value="all">All States</MenuItem>
          {stateOptions.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="College"
          value={collegeFilter}
          onChange={(e) => setCollegeFilter(e.target.value)}
          sx={{ minWidth: 220, backgroundColor: 'background.paper' }}
        >
          <MenuItem value="all">All Colleges</MenuItem>
          {collegeIndex.options.map((c) => (
            <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
          ))}
        </TextField>
        {(search || stateFilter !== 'all' || collegeFilter !== 'all') && (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {filteredInterns.length} of {interns.length}
          </Typography>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && filteredInterns.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No interns match this view</Typography>
            </Paper>
          )}
          {!loading && filteredInterns.map((i) => (
            <Paper
              key={i.id || i.intern_id || i.email}
              sx={{ p: 2, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={() => handleRowClick({ row: i })}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                    {(i.first_name || '') + ' ' + (i.last_name || '') || i.email || '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{i.email}</Typography>
                </Box>
                <StatusChip status={i.status} type="intern" />
              </Box>
              {(collegeOf(i) || stateOf(i)) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {[collegeOf(i), stateOf(i)].filter(Boolean).join(' — ')}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Applied: {formatDate(i.created_at || i.applied_at)}
              </Typography>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 600, backgroundColor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={filteredInterns}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id || row.intern_id || row.email}
            onRowClick={handleRowClick}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              // Default: newest applications at the top. Click any column
              // header to override; multi-sort via Shift-click.
              sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
            }}
            disableRowSelectionOnClick
          />
        </Box>
      )}

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 420 } } }}
      >
        {selectedIntern && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Intern Details</Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <DetailRow label="Name" value={selectedIntern.name || `${selectedIntern.first_name || ''} ${selectedIntern.last_name || ''}`} />
              <DetailRow label="Email" value={selectedIntern.email} />
              <DetailRow label="College" value={collegeOf(selectedIntern) || selectedIntern.college_id} />
              <DetailRow
                label="Location"
                value={[selectedIntern.address?.city, stateOf(selectedIntern), selectedIntern.address?.zip].filter(Boolean).join(', ')}
              />
              <DetailRow label="GPA" value={selectedIntern.gpa} />
              <DetailRow label="Major" value={selectedIntern.major} />
              <DetailRow label="Phone" value={selectedIntern.phone} />
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip status={selectedIntern.status} type="intern" />
                </Box>
              </Box>
              <DetailRow label="Created" value={formatDate(selectedIntern.created_at)} />
              {selectedIntern.referral_code && (
                <DetailRow label="Referral Code" value={selectedIntern.referral_code} />
              )}
              {selectedIntern.under_review_for_cancellations && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Flagged for review — this intern hit the 30-day cancellation limit
                  and cannot cancel further bookings until cleared.
                </Alert>
              )}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <VideoReviewPanel
              intern={selectedIntern}
              videoDetail={videoDetail}
              videoLoading={videoLoading}
              actionLoading={actionLoading}
              onApprove={() => setConfirmAction('approve_video')}
              onReject={() => {
                setRejectVideoReason('');
                setRejectVideoOpen(true);
              }}
            />

            <Divider sx={{ my: 3 }} />

            <TestStatusPanel intern={selectedIntern} />

            <Divider sx={{ my: 3 }} />

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setConfirmAction('approved')}
                fullWidth
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setConfirmAction('rejected')}
                fullWidth
              >
                Reject
              </Button>
            </Stack>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<LockResetIcon />}
              onClick={() => setConfirmAction('reset_password')}
              fullWidth
            >
              Reset Password
            </Button>
            {selectedIntern.under_review_for_cancellations && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<FlagCircleIcon />}
                onClick={() => setConfirmAction('clear_cancel_flag')}
                fullWidth
                sx={{ mt: 2 }}
              >
                Clear Cancellation Flag
              </Button>
            )}
            {(selectedIntern.assessment_test || selectedIntern.status === 'test_failed_permanently') && (
              <Button
                variant="outlined"
                color="info"
                onClick={() => setConfirmAction('reset_test')}
                fullWidth
                sx={{ mt: 2 }}
              >
                Reset Onboarding Test
              </Button>
            )}
          </Box>
        )}
      </Drawer>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmAction}
        title={
          confirmAction === 'approved' ? 'Approve Intern' :
          confirmAction === 'reset_password' ? 'Reset Intern Password' :
          confirmAction === 'clear_cancel_flag' ? 'Clear Cancellation Flag' :
          confirmAction === 'reset_test' ? 'Reset Onboarding Test' :
          confirmAction === 'approve_video' ? 'Approve Video Introduction' :
          'Reject Intern'
        }
        message={
          <Box>
            <Typography sx={{ mb: 2 }}>
              {confirmAction === 'approved'
                ? 'Are you sure you want to approve this intern? They will be added to the intern Cognito group and can log into the intern portal.'
                : confirmAction === 'reset_password'
                ? `This will send a new temporary password to ${selectedIntern?.email} and require them to set a new password on next login. Continue?`
                : confirmAction === 'clear_cancel_flag'
                ? 'This clears the under-review flag and resets the intern\'s 30-day cancel history to zero. They will be able to cancel bookings again. Add an internal note for the audit log if helpful.'
                : confirmAction === 'reset_test'
                ? (selectedIntern?.status === 'test_failed_permanently'
                    ? 'This wipes the intern\'s test history (status, attempts, scores) AND flips their status back to pending_assessment so they can retake the test. Use for legitimate retake exceptions only.'
                    : 'This wipes the intern\'s test history (status, attempts, scores, cooldown, and used-question list). They will be able to start a fresh attempt immediately. Use for system errors or legitimate retake exceptions.')
                : confirmAction === 'approve_video'
                ? 'Approve this intern\'s video introduction? If they\'re in a college cohort, they\'ll be auto-approved as an intern and emailed login details. Otherwise, they\'ll be routed to a background check.'
                : 'Are you sure you want to reject this intern?'}
            </Typography>
            {(confirmAction === 'rejected' || confirmAction === 'clear_cancel_flag' || confirmAction === 'reset_test') && (
              <TextField
                label={confirmAction === 'rejected' ? 'Reason (optional)' : 'Internal note (optional)'}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            )}
          </Box>
        }
        confirmText={
          confirmAction === 'approved' ? 'Approve' :
          confirmAction === 'reset_password' ? 'Reset Password' :
          confirmAction === 'clear_cancel_flag' ? 'Clear Flag' :
          confirmAction === 'reset_test' ? 'Reset Test' :
          confirmAction === 'approve_video' ? 'Approve Video' :
          'Reject'
        }
        confirmColor={
          confirmAction === 'approved' ? 'success' :
          confirmAction === 'reset_password' ? 'warning' :
          confirmAction === 'clear_cancel_flag' ? 'warning' :
          confirmAction === 'reset_test' ? 'info' :
          confirmAction === 'approve_video' ? 'success' :
          'error'
        }
        onConfirm={() => {
          if (confirmAction === 'reset_password') {
            handleResetPassword();
          } else if (confirmAction === 'clear_cancel_flag') {
            handleClearCancelFlag();
          } else if (confirmAction === 'reset_test') {
            handleResetTest();
          } else if (confirmAction === 'approve_video') {
            handleApproveVideo();
          } else {
            handleStatusUpdate(confirmAction);
          }
        }}
        onCancel={() => {
          setConfirmAction(null);
          setReason('');
        }}
      />

      {/* Reject Video dialog — separate from the generic ConfirmDialog
          because the rejection reason is REQUIRED and goes into the
          email sent to the intern. */}
      <ConfirmDialog
        open={rejectVideoOpen}
        title="Reject Video Introduction"
        message={
          <Box>
            <Typography sx={{ mb: 2 }}>
              The intern will receive an email with this reason and a
              prompt to record + upload a new video. Be specific so they
              know what to fix.
            </Typography>
            <TextField
              label="Rejection reason (required, visible to intern)"
              value={rejectVideoReason}
              onChange={(e) => setRejectVideoReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              required
              autoFocus
              placeholder="e.g. Audio is too quiet to hear. Please re-record in a quieter room."
            />
          </Box>
        }
        confirmText="Reject & Notify"
        confirmColor="error"
        onConfirm={handleRejectVideoSubmit}
        onCancel={() => {
          setRejectVideoOpen(false);
          setRejectVideoReason('');
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>

      {actionLoading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 9999 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

// Renders the intern's intro video for admin review + the Approve/Reject
// controls. Prefers `videoDetail` (from get_intern_video) over the row
// data because the row may be stale.
function VideoReviewPanel({ intern, videoDetail, videoLoading, actionLoading, onApprove, onReject }) {
  // Prefer the live detail fetch over the (possibly stale) list row.
  const data = videoDetail || intern || {};
  const videoUrl = data.video_url || intern?.video_url;
  const status = data.video_status || (videoUrl ? 'pending' : 'none');
  const uploadedAt = data.video_uploaded_at || intern?.video_uploaded_at;
  const reviewedAt = data.video_reviewed_at;
  const reviewedBy = data.video_reviewed_by;
  const rejectionReason = data.video_rejection_reason;
  const rejectedAt = data.video_rejected_at;

  const formatDateTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(String(iso).replace('Z', '')).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (!videoUrl) {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Video Introduction
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No video uploaded yet.
        </Typography>
      </Box>
    );
  }

  const showActions = status === 'pending';

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Video Introduction
      </Typography>

      <Box
        component="video"
        controls
        preload="metadata"
        src={videoUrl}
        sx={{
          width: '100%',
          maxHeight: 280,
          borderRadius: 1,
          backgroundColor: '#000',
          mb: 1.5,
        }}
      >
        Your browser cannot play this video.{' '}
        <a href={videoUrl} target="_blank" rel="noopener noreferrer">Download instead</a>
      </Box>

      {uploadedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Uploaded: {formatDateTime(uploadedAt)}
        </Typography>
      )}

      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Status
        </Typography>
        <StatusChip status={status} type="videoStatus" />
        {videoLoading && (
          <CircularProgress size={12} sx={{ ml: 1, verticalAlign: 'middle' }} />
        )}
      </Box>

      {(status === 'approved' || status === 'rejected') && reviewedBy && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {status === 'approved' ? 'Approved' : 'Reviewed'} by{' '}
          <strong>{reviewedBy}</strong>
          {reviewedAt ? ` on ${formatDateTime(reviewedAt)}` : ''}
        </Typography>
      )}

      {status === 'rejected' && rejectionReason && (
        <Alert severity="warning" sx={{ mt: 1.5, mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
            Reason sent to intern
            {rejectedAt ? ` (${formatDateTime(rejectedAt)})` : ''}:
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {rejectionReason}
          </Typography>
        </Alert>
      )}

      {showActions && (
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={onApprove}
            disabled={actionLoading}
            fullWidth
          >
            Approve Video
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={onReject}
            disabled={actionLoading}
            fullWidth
          >
            Reject Video
          </Button>
        </Stack>
      )}
    </Box>
  );
}

function TestStatusPanel({ intern }) {
  const test = intern?.assessment_test;
  if (!test || (!test.status && !test.attempt_count)) {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Onboarding Test</Typography>
        <Typography variant="body2" color="text.secondary">Not started.</Typography>
      </Box>
    );
  }
  const status = test.status || 'not_started';
  const attempts = test.attempt_count || 0;
  const lastPct = typeof test.last_percentage === 'number' ? test.last_percentage : Number(test.last_percentage || 0);
  const lastScore = test.last_score;
  const lastDate = test.last_attempt_date;
  const domains = test.last_domain_scores || {};
  const history = test.history || [];
  const nextEligibleAt = test.next_eligible_at;
  const inProgress = !!(test.current_test && test.current_test.test_id);

  const statusColor = (() => {
    if (status === 'passed') return 'success';
    if (status === 'permanently_ineligible') return 'error';
    if (status === 'failed') return 'warning';
    if (inProgress || status === 'in_progress') return 'info';
    return 'default';
  })();

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Onboarding Test</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={status.replace(/_/g, ' ')}
          color={statusColor}
          size="small"
          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
        />
        {inProgress && <Chip label="In progress" color="info" size="small" />}
        <Chip label={`Attempt ${attempts} of 4`} size="small" />
      </Stack>

      {(lastScore != null || lastPct > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Last score: <strong>{lastScore != null ? `${lastScore}/40` : '—'} ({lastPct}%)</strong>
          </Typography>
          {lastDate && (
            <Typography variant="caption" color="text.secondary">
              {new Date(String(lastDate).replace('Z', '')).toLocaleString()}
            </Typography>
          )}
        </Box>
      )}

      {Object.keys(domains).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Last attempt — per-domain breakdown
          </Typography>
          <Stack spacing={1}>
            {Object.entries(domains).map(([key, d]) => {
              const pct = typeof d.percentage === 'number' ? d.percentage : Number(d.percentage || 0);
              const weak = pct < 75;
              return (
                <Box key={key}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography variant="caption">{d.name || key}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.correct ?? 0}/{d.total ?? 0} ({pct}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, pct)}
                    color={weak ? 'error' : 'success'}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {status === 'failed' && nextEligibleAt && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Can retake on {new Date(String(nextEligibleAt).replace('Z', '')).toLocaleString()}.
        </Alert>
      )}

      {status === 'permanently_ineligible' && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          Permanently ineligible — 4 attempts used.
        </Alert>
      )}

      {history.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Attempt history
          </Typography>
          <Stack spacing={0.5}>
            {history.map((h) => {
              const pct = typeof h.percentage === 'number' ? h.percentage : Number(h.percentage || 0);
              const dateStr = h.submitted_at ? new Date(String(h.submitted_at).replace('Z', '')).toLocaleDateString() : '—';
              return (
                <Box key={h.attempt_number} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption">
                    #{h.attempt_number} on {dateStr}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: h.passed ? 'success.main' : 'error.main', fontWeight: 600 }}>
                      {h.score}/{h.total} ({pct}%)
                    </Typography>
                    <Chip
                      label={h.passed ? 'pass' : 'fail'}
                      size="small"
                      color={h.passed ? 'success' : 'error'}
                      sx={{ height: 16, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
